import React, { Component } from "react";
import ReactApexChart from 'react-apexcharts';
import axios from 'axios';
import backend_url from "../../../routes/API_URL";
import swal from 'sweetalert';
import moment from 'moment';

const API_URL = backend_url['api_url'];

class SchoolProgressGraph extends Component {
  constructor(props) {
    super(props);
    
    const school_id = this.props.school_id;
    const end_date = new Date().getFullYear();
    const start_date = end_date - 3;
    
    this.state = {
      GraphicalData: [],
      VisitData: [],
      dataType: 'levels',
      bearer_auth: window.sessionStorage.getItem('token'),
      school_id: school_id,
      school_name: '',
      start_date: props.start ? new Date(props.start).getFullYear() : start_date,
      end_date: props.end ? new Date(props.end).getFullYear() : end_date,
      start: props.start ? moment(props.start).startOf('year').format("YYYY-MM-DD") : moment().subtract(3, 'years').startOf('year').format("YYYY-MM-DD"),
      end: props.end ? moment(props.end).endOf('year').format("YYYY-MM-DD") : moment().endOf('year').format("YYYY-MM-DD"),
      loading: false,
      error: null
    };
  }

  componentDidMount() {
    this.fetchProgressData();
  }

  componentDidUpdate(prevProps) {
    if (this.props.school_id !== prevProps.school_id) {
      this.setState({ school_id: this.props.school_id }, () => {
        this.fetchProgressData();
      });
    }
  }

  fetchProgressData() {
    const { school_id, bearer_auth } = this.state;
    
    if (!school_id || school_id === 'undefined' || school_id === 'null' || school_id === '') {
      this.setState({ 
        error: 'Invalid school ID',
        loading: false
      });
      return;
    }

    this.setState({ loading: true, error: null });

    // Fetch level achievements
    Promise.all([
      axios.get(`${API_URL}/school_level_achievements/${school_id}`, 
        { headers: { "Authorization": `Bearer ${bearer_auth}` } }
      ),
      axios.get(`${API_URL}/schools/${school_id}`,
        { headers: { "Authorization": `Bearer ${bearer_auth}` } }
      )
    ]).then(([level_response, school_response]) => {
      const levelData = level_response.data.data || [];
      
      this.setState({
        GraphicalData: levelData,
        dataType: levelData.length > 0 ? 'levels' : 'visits',
        school_name: school_response.data[0]?.['school_name'] || 'Unknown School',
        loading: false
      });

      // If no level data, fetch visit stats as fallback
      if (levelData.length === 0) {
        this.fetchVisitStats();
      }
    }).catch(error => {
      console.error('Error loading progress data:', error);
      
      if (error.response?.status === 401) {
        swal({
          title: "",
          text: "Your session has expired. You'll automatically be logged out within seconds.",
          icon: "warning",
          timer: 2000
        }).then(() => {
          window.sessionStorage.clear();
          window.location.href = '/login';
        });
      } else {
        // Fall back to visit stats
        this.fetchVisitStats();
      }
    });
  }

  fetchVisitStats() {
    const { school_id, bearer_auth } = this.state;
    
    axios.get(`${API_URL}/school_inspection_stats/${school_id}`, 
      { headers: { "Authorization": `Bearer ${bearer_auth}` } }
    ).then(response => {
      const visitData = response.data.data || [];
      this.setState({
        VisitData: visitData,
        dataType: visitData.length > 0 ? 'visits' : 'none',
        loading: false
      });
    }).catch(error => {
      console.error('Error fetching visit stats:', error);
      this.setState({ 
        dataType: 'none',
        loading: false 
      });
    });
  }

  render() {
    const { GraphicalData, VisitData, dataType, school_name, start_date, end_date, loading, error } = this.state;

    if (loading) {
      return (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2">Loading progress data...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-5">
          <h5 className="text-danger">{error}</h5>
          <p>Unable to load progress data for this school.</p>
        </div>
      );
    }

    // Show level achievement chart
    if (dataType === 'levels' && GraphicalData.length > 0) {
      // Get unique years
      const years = [...new Set(GraphicalData.map(d => d.DATE))].sort();
      
      // Initialize level data arrays
      let level_one_data = new Array(years.length).fill(0);
      let level_two_data = new Array(years.length).fill(0);
      let level_three_data = new Array(years.length).fill(0);
      let level_four_data = new Array(years.length).fill(0);
      
      // Calculate percentages per year
      years.forEach((year, yearIndex) => {
        const yearData = GraphicalData.filter(d => d.DATE === year);
        const total = yearData.reduce((sum, d) => sum + d.COUNT, 0);
        
        yearData.forEach(d => {
          const percentage = (d.COUNT / total) * 100;
          const level = parseInt(d.level_achieved);
          
          if (level === 1) level_one_data[yearIndex] = percentage;
          else if (level === 2) level_two_data[yearIndex] = percentage;
          else if (level === 3) level_three_data[yearIndex] = percentage;
          else if (level === 4) level_four_data[yearIndex] = percentage;
        });
      });

      const series = [{
        name: 'Level 1 - Does not meet minimum standards',
        data: level_one_data
      }, {
        name: 'Level 2 - Meets Minimum Standards',
        data: level_two_data
      }, {
        name: 'Level 3 - Exceeds Minimum Standards',
        data: level_three_data
      }, {
        name: 'Level 4 - Effective Practice',
        data: level_four_data
      }];

      const options = {
        chart: {
          type: 'bar',
          height: 500,
          stacked: true,
          stackType: '100%',
          toolbar: { show: true }
        },
        colors: ['#ff0000', '#0018ff', '#fffc00', '#1bb60b'],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '70%',
            borderRadius: 4
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (value) => value ? value.toFixed(1) + '%' : '0%',
          style: { fontSize: '11px', colors: ['#fff'] }
        },
        title: {
          text: school_name 
            ? `${school_name} - Achievement Levels (${start_date} to ${end_date})`
            : `Achievement Levels (${start_date} to ${end_date})`,
          align: 'center',
          style: { fontSize: '16px', fontWeight: 'bold' }
        },
        xaxis: {
          categories: years,
          title: { text: 'INSPECTION YEAR' }
        },
        yaxis: {
          title: { text: 'PERCENTAGE OF ACHIEVEMENT' },
          max: 100,
          labels: { formatter: (value) => value ? value.toFixed(0) + '%' : '0%' }
        },
        tooltip: {
          y: { formatter: (value) => value ? value.toFixed(2) + '%' : '0%' }
        },
        legend: {
          position: 'bottom',
          horizontalAlign: 'center',
          fontSize: '12px'
        }
      };

      return (
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <ReactApexChart options={options} series={series} type="bar" height={500} />
            </div>
          </div>
        </div>
      );
    }
    
    // Show visit count chart (fallback)
    else if (dataType === 'visits' && VisitData.length > 0) {
      const years = VisitData.map(d => d.YEAR).sort();
      const counts = years.map(year => {
        const data = VisitData.find(d => d.YEAR === year);
        return data ? data.visit_count : 0;
      });

      const series = [{
        name: 'Number of Inspections',
        data: counts
      }];

      const options = {
        chart: { type: 'bar', height: 500 },
        colors: ['#3498db'],
        plotOptions: { bar: { columnWidth: '55%' } },
        title: {
          text: school_name ? `${school_name} - Inspection History` : 'Inspection History',
          align: 'center'
        },
        xaxis: { categories: years, title: { text: 'YEAR' } },
        yaxis: { title: { text: 'NUMBER OF INSPECTIONS' } }
      };

      return (
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <ReactApexChart options={options} series={series} type="bar" height={500} />
            </div>
          </div>
        </div>
      );
    }

    // No data at all
    return (
      <div className="text-center p-5">
        <h5>No Progress Data Available</h5>
        <p>There is no inspection data available for this school in the selected period.</p>
      </div>
    );
  }
}

export default SchoolProgressGraph;